/**
 * ADMIN PRICING DASHBOARD - Unified for All Service Types
 * Handles pricing configuration for:
 * - Service Type 3: Airbnb Cleaning
 * - Service Type 4: Residential Property Cleaning
 * - Service Type 5: Commercial Office Cleaning
 * - Service Type 6: Event Venue Cleaning
 * - Service Type 7: HMO Cleaning
 * - Service Types 9-18: Carpentry & Joinery (Bespoke Joinery, Cabinetry & Storage, Carpentry 1st/2nd Fix, 
 *   Door Installation, Flooring, Hoarding Systems, Roofing Carpentry, Skirting & Architraves, Staircases)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import apiService from '@/services/api';
import API_CONFIG from '@/config';
import './AdminCleaningPricingRules.css';

const SERVICE_TYPES = {
  3: { name: 'Airbnb Cleaning', color: '#EF4444' },
  4: { name: 'Residential Property', color: '#3B82F6' },
  5: { name: 'Commercial Office', color: '#10B981' },
  6: { name: 'Event Venue', color: '#F59E0B' },
  7: { name: 'HMO Cleaning', color: '#8B5CF6' },
  // Carpentry & Joinery Services
  9: { name: 'Bespoke Joinery', color: '#D946EF' },
  10: { name: 'Cabinetry & Storage', color: '#EC4899' },
  11: { name: 'Carpentry (1st Fix)', color: '#F43F5E' },
  12: { name: 'Carpentry (2nd Fix)', color: '#FB923C' },
  13: { name: 'Door Installation', color: '#FBBF24' },
  14: { name: 'Flooring (Wood & Laminate)', color: '#A3E635' },
  15: { name: 'Hoarding Systems', color: '#4ADE80' },
  16: { name: 'Roofing Carpentry', color: '#2DD4BF' },
  17: { name: 'Skirting & Architraves', color: '#06B6D4' },
  18: { name: 'Staircases', color: '#0EA5E9' },
  // New Services - Categories 24, 25, 26
  24: { name: 'Specialist & Compliance', color: '#EF4444' },
  25: { name: 'Waste & Clearance', color: '#92400E' },
  26: { name: 'Windows, Doors & Glazing', color: '#1E40AF' }
};

interface AdminCleaningPricingRulesProps {
  isTabMode?: boolean;
  serviceType?: number;
}

const AdminCleaningPricingRules = ({ isTabMode = false, serviceType: propServiceType }: AdminCleaningPricingRulesProps) => {
  const { serviceTypeId: urlServiceTypeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [corrections, setCorrections] = useState(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [questionEdits, setQuestionEdits] = useState<Record<string, any>>({});
  const [selectedTab, setSelectedTab] = useState('overview');
  
  // Use prop if provided (tab mode), URL parameter if available, otherwise default to 3 (Airbnb)
  const [selectedServiceType, setSelectedServiceType] = useState(propServiceType || parseInt(urlServiceTypeId) || 3);
  const currentServiceType = isTabMode ? (propServiceType || selectedServiceType) : (parseInt(urlServiceTypeId) || 3);

  // Redirect to default service type if none provided - but ONLY when used as a route (not as a tab)
  useEffect(() => {
    if (!isTabMode && !urlServiceTypeId) {
      navigate('/admin/pricing-rules/3', { replace: true });
    }
  }, [urlServiceTypeId, navigate, isTabMode]);

  useEffect(() => {
    fetchPricingConfig();
    fetchStatistics();
    fetchQuestions();
    if (currentServiceType === 3) {
      fetchCorrections();
    }
  }, [currentServiceType]);

  const fetchPricingConfig = async () => {
    try {
      setLoading(true);
      // Use /admin/pricing-extended for types 4-7 and 9-18 (Carpentry), /admin/pricing for type 3
      const endpoint = currentServiceType === 3 ? `/admin/pricing/config/${currentServiceType}` : `/admin/pricing-extended/config/${currentServiceType}`;
      const response = await apiService.request(endpoint, 'GET');
      if (response.success) {
        // Check if this is new service type response (categories 24, 25, 26)
        if (response.data && Array.isArray(response.data)) {
          // New format: { success, category_id, count, data: [nodes with options] }
          setConfig({
            nodes: response.data,
            category_id: response.category_id,
            count: response.count,
            isNewFormat: true
          });
        } else {
          // Legacy format: { success, multipliers, fixedCosts, floorAreaMappings }
          setConfig({
            multipliers: response.multipliers,
            fixedCosts: response.fixedCosts,
            floorAreaMappings: response.floorAreaMappings,
            isNewFormat: false
          });
        }
      }
    } catch (err) {
      console.error('Error fetching pricing config:', err);
      alert('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      // Use /admin/pricing-extended for types 4-7 and 9-18 (Carpentry), /admin/pricing for type 3
      const endpoint = currentServiceType === 3 ? `/admin/pricing/statistics/${currentServiceType}` : `/admin/pricing-extended/statistics/${currentServiceType}`;
      const response = await apiService.request(endpoint, 'GET');
      if (response.success) {
        // API returns data at top level
        setStats(response.data || {
          multipliers: response.multipliers || 0,
          fixedCosts: response.fixedCosts || 0,
          floorMappings: response.floorMappings || 0,
          quotesGenerated: response.quotesGenerated || 0
        });
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const fetchQuestions = async () => {
    try {
      const endpoint = `/admin/pricing-extended/questions/${currentServiceType}`;
      const response = await apiService.request(endpoint, 'GET');
      if (response.success) {
        // Normalize decision-node fields for types 24/25/26
        const raw = response.questions || [];
        const normalized = [24, 25, 26].includes(currentServiceType)
          ? raw.map((q: any) => ({
              ...q,
              question_id: String(q.id),
              question_label: q.label || q.node_title,
              question_text: q.question || q.node_question,
              question_type: 'decision_node',
              display_order: q.node_number,
              options: null,
              is_required: true,
            }))
          : raw;
        setQuestions(normalized);
        const edits: Record<string, any> = {};
        normalized.forEach((q: any) => {
          edits[q.question_id] = {
            label: q.question_label,
            options: q.options ? JSON.stringify(q.options, null, 2) : '',
            isRequired: q.is_required,
          };
        });
        setQuestionEdits(edits);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
    }
  };

  const handleQuestionSave = async (questionId: string) => {
    try {
      const edit = questionEdits[questionId];
      if (!edit) return;
      let parsedOptions = null;
      if (edit.options && edit.options.trim()) {
        try {
          parsedOptions = JSON.parse(edit.options);
        } catch {
          alert('Options JSON is invalid. Please fix before saving.');
          return;
        }
      }
      await apiService.request(`/admin/pricing-extended/questions/${questionId}`, 'PUT', {
        label: edit.label,
        options: parsedOptions,
        isRequired: edit.isRequired
      });
      alert('Question updated successfully');
      fetchQuestions();
    } catch (err) {
      console.error('Error saving question:', err);
      alert('Failed to save question');
    }
  };

  const fetchCorrections = async () => {
    try {
      const endpoint = `/admin/pricing/corrections/${currentServiceType}`;
      const response = await apiService.request(endpoint, 'GET');
      if (response.success) {
        setCorrections(response);
      }
    } catch (err) {
      console.error('Error fetching corrections:', err);
    }
  };

  const handleMultiplierUpdate = async (multiplierType, key, newValue) => {
    try {
      const endpoint = currentServiceType === 3 ? `/admin/pricing/multiplier` : `/admin/pricing-extended/multiplier`;
      await apiService.request(
        endpoint,
        'PUT',
        {
          serviceTypeId: currentServiceType,
          multiplierType,
          multiplierKey: key,
          multiplierValue: parseFloat(newValue)
        }
      );
      alert('Multiplier updated successfully');
      fetchPricingConfig();
    } catch (err) {
      console.error('Error updating multiplier:', err);
      alert('Failed to update multiplier');
    }
  };

  const handleFixedCostUpdate = async (costType, costItem, newValue) => {
    try {
      const endpoint = currentServiceType === 3 ? `/admin/pricing/fixed-cost` : `/admin/pricing-extended/fixed-cost`;
      await apiService.request(
        endpoint,
        'PUT',
        {
          serviceTypeId: currentServiceType,
          costType,
          costItem,
          costValue: parseFloat(newValue)
        }
      );
      alert('Fixed cost updated successfully');
      fetchPricingConfig();
    } catch (err) {
      console.error('Error updating fixed cost:', err);
      alert('Failed to update fixed cost');
    }
  };

  if (loading) {
    return (
      <div className="admin-pricing-page" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading pricing configuration...</p>
      </div>
    );
  }

  const serviceName = SERVICE_TYPES[currentServiceType]?.name || 'Unknown Service';
  const serviceColor = SERVICE_TYPES[currentServiceType]?.color || '#3B82F6';

  return (
    <div className="admin-pricing-page">
      <div className="page-header" style={{ borderTopColor: serviceColor }}>
        <h1>Pricing Rules - {serviceName}</h1>
        <p>Configure pricing multipliers, fixed costs, and floor area mappings</p>
      </div>

      <div style={{ padding: '2rem' }}>
        {/* Service Type Selector */}
        <div className="service-selector">
          <Label>Switch Service Type:</Label>
          <div className="button-group">
            {Object.entries(SERVICE_TYPES).map(([id, { name }]) => (
              <Button
                key={id}
                variant={parseInt(id) === currentServiceType ? 'default' : 'outline'}
                onClick={() => {
                  if (isTabMode) {
                    setSelectedServiceType(parseInt(id));
                  } else {
                    navigate(`/admin/pricing-rules/${id}`);
                  }
                }}
                style={{
                  backgroundColor: parseInt(id) === currentServiceType ? '#3B82F6' : 'transparent',
                  color: parseInt(id) === currentServiceType ? 'white' : '#3B82F6',
                  border: `1px solid ${parseInt(id) === currentServiceType ? '#3B82F6' : '#d1d5db'}`,
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  transition: 'all 0.2s'
                }}
              >
                {name}
              </Button>
            ))}
          </div>
        </div>

        {/* Statistics Overview */}
        {stats && (
          <Card className="stats-card">
            <div className="stat-item">
              <h4>{stats.multipliers}</h4>
              <p>Multiplier Rules</p>
            </div>
            <div className="stat-item">
              <h4>{stats.fixedCosts}</h4>
              <p>Fixed Costs</p>
            </div>
            <div className="stat-item">
              <h4>{stats.floorMappings}</h4>
              <p>Floor Area Tiers</p>
            </div>
            <div className="stat-item">
              <h4>{stats.quotesGenerated}</h4>
              <p>Quotes Generated</p>
            </div>
          </Card>
        )}

        {/* Configuration Tabs */}
      {config && (
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="tabs-list">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="multipliers">Multipliers</TabsTrigger>
            <TabsTrigger value="fixed-costs">Fixed Costs</TabsTrigger>
            {/* Floor Mappings for area-based pricing (types 3, 5, 6, 7) - NOT for Carpentry (9-18) */}
            {(currentServiceType === 3 || currentServiceType === 5 || currentServiceType === 6 || currentServiceType === 7) && (
              <TabsTrigger value="floor-mappings">Floor Mappings</TabsTrigger>
            )}
            {/* Questions tab for all service types */}
            <TabsTrigger value="questions">Questions ({questions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="tab-content">
            <Card>
              <h3>Configuration Overview</h3>
              <p>Service Type: {serviceName}</p>
              <p>Total Multiplier Rules: {stats?.multipliers || 0}</p>
              <p>Total Fixed Costs: {stats?.fixedCosts || 0}</p>
              <p>Floor Area Mappings: {stats?.floorMappings || 0}</p>
              <p>Quotes Generated: {stats?.quotesGenerated || 0}</p>
            </Card>

            {currentServiceType === 3 && corrections && corrections.categories && (
              <Card style={{ marginTop: '2rem' }}>
                <h3 style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem', marginBottom: '1rem' }}>Global Variables</h3>
                {corrections.categories.find(c => c.name.includes('Global Variables'))?.items.map((item, idx) => {
                  const isComplete = item.status === '✅';
                  return (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      marginBottom: idx < 2 ? '0.75rem' : '0',
                      backgroundColor: isComplete ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${isComplete ? '#bbf7d0' : '#fecaca'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{item.status}</span> {item.name}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                          Expected: {item.expected} | Current: {item.actual}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
          </TabsContent>

          <TabsContent value="multipliers" className="tab-content">
            {currentServiceType === 3 && corrections && corrections.categories && (
              <Card style={{ marginBottom: '2rem' }}>
                <h3 style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem', marginBottom: '1rem' }}>Quote Type Corrections</h3>
                <div>
                  {corrections.categories.find(c => c.name.includes('Soiling'))?.items.slice(2, 3).map((item, idx) => (
                    <div key={idx} style={{
                      padding: '0.75rem',
                      backgroundColor: item.status === '✅' ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${item.status === '✅' ? '#bbf7d0' : '#fecaca'}`,
                      borderRadius: '4px',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span>{item.status}</span>
                        <strong>{item.name}</strong>
                      </div>
                      <div style={{ color: '#666', fontSize: '0.8rem' }}>
                        {item.expected} → {item.actual}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            <Card>
              <h3>Multiplier Rules</h3>
              {currentServiceType === 4 && (
                <div style={{ 
                  padding: '1rem', 
                  marginBottom: '1rem', 
                  backgroundColor: '#f0f4ff', 
                  border: '1px solid #c7d2fe', 
                  borderRadius: '4px'
                }}>
                  <strong>📐 TWO-LAYER MULTIPLIER SYSTEM</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                    Residential pricing combines two service multiplier layers:
                  </p>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                    <div>🔹 <strong>service_type</strong> (Base): regular=1.00, deep=1.25, eot=1.20, ab=1.35</div>
                    <div>🔹 <strong>service_time</strong> (Time): regular=1.00, deep=1.20, eot=1.15, ab=1.30</div>
                    <div style={{ marginTop: '0.5rem' }}>✖️ Combined: deep_cleaning = 1.25 × 1.20 = 1.50x</div>
                  </div>
                </div>
              )}
              <div className="rules-grid">
                {config.isNewFormat && config.nodes ? (
                  // Types 24/25/26: multipliers live inside decision-node options
                  (config.nodes as any[]).map((node: any, nIdx: number) => {
                    if (!node.options?.length) return null;
                    const multiplierCols = Object.keys(node.options[0]).filter(
                      k => k.endsWith('_multiplier')
                    );
                    if (!multiplierCols.length) return null;
                    return (
                      <div key={nIdx} className="rule-section">
                        <h4>Node {node.node_number}: {node.node_title}</h4>
                        <table className="rules-table">
                          <thead>
                            <tr>
                              <th>Option</th>
                              {multiplierCols.map(col => (
                                <th key={col}>{col.replace(/_multiplier$/, '').replace(/_/g, ' ')}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(node.options as any[]).map((opt: any, oIdx: number) => (
                              <tr key={oIdx}>
                                <td>{opt.option_label}</td>
                                {multiplierCols.map(col => (
                                  <td key={col}>{opt[col]}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                ) : (
                  config.multipliers && Array.isArray(config.multipliers) &&
                  Object.values(
                    config.multipliers.reduce((acc, rule) => {
                      const type = rule.multiplier_type || 'Other';
                      if (!acc[type]) acc[type] = [];
                      acc[type].push(rule);
                      return acc;
                    }, {})
                  ).map((rules, idx) => (
                    <div key={idx} className="rule-section">
                      <h4>{(rules[0]?.multiplier_type || 'Other').replace(/_/g, ' ').toUpperCase()}</h4>
                      <table className="rules-table">
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Value</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rules.map((rule) => (
                            <tr key={`${rule.multiplier_type}_${rule.multiplier_key}`}>
                              <td>{rule.multiplier_key}</td>
                              <td>
                                <Input
                                  type="number"
                                  step="0.01"
                                  defaultValue={rule.multiplier_value}
                                  onChange={(e) => handleMultiplierUpdate(rule.multiplier_type, rule.multiplier_key, e.target.value)}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td className="description">{rule.description || '-'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  onClick={() => handleMultiplierUpdate(rule.multiplier_type, rule.multiplier_key, rule.multiplier_value)}
                                >
                                  Save
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="fixed-costs" className="tab-content">
            {currentServiceType === 3 && corrections && corrections.categories && (
              <Card style={{ marginBottom: '2rem' }}>
                <h3 style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem', marginBottom: '1rem' }}>Corrections Status</h3>
                <div className="rules-grid">
                  <div className="rule-section">
                    <h4>Soiling Treatments</h4>
                    <table className="rules-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Current Value</th>
                          <th>Expected</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {corrections.categories.find(c => c.name.includes('Soiling'))?.items.slice(0, 2).map((item, idx) => (
                          <tr key={idx} style={{ backgroundColor: item.status === '✅' ? '#f0fdf4' : '#fef2f2' }}>
                            <td><strong>{item.name}</strong></td>
                            <td>
                              <Input
                                type="text"
                                defaultValue={item.actual}
                                style={{ width: '120px' }}
                              />
                            </td>
                            <td style={{ fontSize: '0.9rem' }}>{item.expected}</td>
                            <td style={{ textAlign: 'center', fontSize: '1.1rem' }}>{item.status}</td>
                            <td>
                              <Button size="sm">Edit</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="rule-section">
                    <h4>Bulk Thresholds</h4>
                    <table className="rules-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Current Value</th>
                          <th>Expected</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {corrections.categories.find(c => c.name.includes('Bulk'))?.items.map((item, idx) => (
                          <tr key={idx} style={{ backgroundColor: item.status === '✅' ? '#f0fdf4' : '#fef2f2' }}>
                            <td><strong>{item.name}</strong></td>
                            <td>
                              <Input
                                type="text"
                                defaultValue={item.actual}
                                style={{ width: '120px' }}
                              />
                            </td>
                            <td style={{ fontSize: '0.9rem' }}>{item.expected}</td>
                            <td style={{ textAlign: 'center', fontSize: '1.1rem' }}>{item.status}</td>
                            <td>
                              <Button size="sm">Edit</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            )}
            <Card>
              <h3>Fixed Costs</h3>
              <div className="rules-grid">
                {config.isNewFormat && config.nodes ? (
                  // Types 24/25/26: fixed costs live as add_cost_gbp on decision-node options
                  (config.nodes as any[]).map((node: any, nIdx: number) => {
                    const costOptions = (node.options || []).filter(
                      (o: any) => Number(o.add_cost_gbp) !== 0 || Number(o.add_cost_min_gbp) || Number(o.add_cost_max_gbp)
                    );
                    if (!costOptions.length) return null;
                    return (
                      <div key={nIdx} className="rule-section">
                        <h4>Node {node.node_number}: {node.node_title}</h4>
                        <table className="rules-table">
                          <thead>
                            <tr>
                              <th>Option</th>
                              <th>Add Cost ($)</th>
                              <th>Min ($)</th>
                              <th>Max ($)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costOptions.map((opt: any, oIdx: number) => (
                              <tr key={oIdx}>
                                <td>{opt.option_label}</td>
                                <td>{opt.add_cost_gbp ?? '-'}</td>
                                <td>{opt.add_cost_min_gbp ?? '-'}</td>
                                <td>{opt.add_cost_max_gbp ?? '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                ) : (
                  config.fixedCosts && Array.isArray(config.fixedCosts) &&
                  Object.values(
                    config.fixedCosts.reduce((acc, cost) => {
                      const type = cost.cost_type || 'Other';
                      if (!acc[type]) acc[type] = [];
                      acc[type].push(cost);
                      return acc;
                    }, {})
                  ).map((costs, idx) => (
                    <div key={idx} className="rule-section">
                      <h4>{(costs[0]?.cost_type || 'Other').replace(/_/g, ' ').toUpperCase()}</h4>
                      <table className="rules-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Value ($)</th>
                            <th>Min</th>
                            <th>Max</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costs.map((cost, costIdx) => (
                            <tr key={`${cost.cost_type}_${cost.cost_item}_${cost.id || costIdx}`}>
                              <td>{cost.cost_item}</td>
                              <td>
                                <Input
                                  type="number"
                                  step="0.01"
                                  defaultValue={cost.cost_value_gbp}
                                  onChange={(e) => handleFixedCostUpdate(cost.cost_type, cost.cost_item, e.target.value)}
                                  style={{ width: '80px' }}
                                />
                              </td>
                              <td>{cost.cost_min || '-'}</td>
                              <td>{cost.cost_max || '-'}</td>
                              <td>
                                <Button
                                  size="sm"
                                  onClick={() => handleFixedCostUpdate(cost.cost_type, cost.cost_item, cost.cost_value_gbp)}
                                >
                                  Save
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Floor Area Mappings Tab - For all service types with floor area pricing */}
          {(currentServiceType === 3 || currentServiceType === 5 || currentServiceType === 6 || currentServiceType === 7) && (
          <TabsContent value="floor-mappings" className="tab-content">
            <Card>
              <h3>Floor Area Mappings (SQM Bands) - SERVICE TYPE {currentServiceType}</h3>
              {config.floorAreaMappings && Array.isArray(config.floorAreaMappings) ? (
                <table className="rules-table">
                  <thead>
                    <tr>
                      <th>Min Area (m²)</th>
                      <th>Max Area (m²)</th>
                      <th>Base Price ($)</th>
                      <th>Display Label</th>
                      <th>Included Area</th>
                    </tr>
                  </thead>
                  <tbody>
                    {config.floorAreaMappings.map((mapping, idx) => (
                      <tr key={idx}>
                        <td>{mapping.area_min_sqm}</td>
                        <td>{mapping.area_max_sqm}</td>
                        <td>£{mapping.base_price_gbp}</td>
                        <td>{mapping.display_label}</td>
                        <td>{mapping.included_sqm || mapping.included_area || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No floor area mappings available</p>
              )}
            </Card>
          </TabsContent>
          )}
          {/* Questions Tab - Shows customer-facing form questions for admin editing */}
          <TabsContent value="questions" className="tab-content">
            <Card>
              <h3 style={{ marginBottom: '0.5rem' }}>Customer Form Questions</h3>
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                These are the questions shown to customers when requesting a quote for {serviceName}.
                You can edit labels, answer options (JSON), and required status.
              </p>
              {questions.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                  <p>No questions configured yet.</p>
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    Run <code>sql/seed-service-form-questions.sql</code> to populate questions from the spec.
                  </p>
                </div>
              ) : (
                <div>
                  {questions.map((q) => {
                    const edit = questionEdits[q.question_id] || { label: q.question_label, options: '', isRequired: q.is_required };
                    return (
                      <div key={q.question_id} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: '#fafafa'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem', fontFamily: 'monospace' }}>
                              {q.question_id}{' · '}type: <strong>{q.question_type}</strong>{' · '}order: {q.display_order}
                            </div>
                            {q.question_text && (
                              <div style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: '0.5rem', fontStyle: 'italic' }}>
                                {q.question_text}
                              </div>
                            )}
                            <Label style={{ marginBottom: '0.25rem', display: 'block' }}>Label</Label>
                            <Input
                              type="text"
                              value={edit.label}
                              onChange={(e) => setQuestionEdits(prev => ({
                                ...prev,
                                [q.question_id]: { ...edit, label: e.target.value }
                              }))}
                              style={{ width: '100%', marginBottom: '0.5rem' }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                            <input
                              type="checkbox"
                              id={`req_${q.question_id}`}
                              checked={edit.isRequired}
                              onChange={(e) => setQuestionEdits(prev => ({
                                ...prev,
                                [q.question_id]: { ...edit, isRequired: e.target.checked }
                              }))}
                            />
                            <Label htmlFor={`req_${q.question_id}`} style={{ cursor: 'pointer', marginBottom: 0 }}>Required</Label>
                          </div>
                        </div>
                        {q.question_type !== 'text' && q.question_type !== 'textarea' && q.question_type !== 'number' && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <Label style={{ marginBottom: '0.25rem', display: 'block' }}>Options (JSON array)</Label>
                            <textarea
                              value={edit.options}
                              onChange={(e) => setQuestionEdits(prev => ({
                                ...prev,
                                [q.question_id]: { ...edit, options: e.target.value }
                              }))}
                              rows={4}
                              style={{
                                width: '100%',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                resize: 'vertical'
                              }}
                            />
                          </div>
                        )}
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                          <Button
                            size="sm"
                            onClick={() => handleQuestionSave(q.question_id)}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setQuestionEdits(prev => ({
                                ...prev,
                                [q.question_id]: {
                                  label: q.question_label,
                                  options: q.options ? JSON.stringify(q.options, null, 2) : '',
                                  isRequired: q.is_required
                                }
                              }));
                            }}
                          >
                            Reset
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}
      </div>
    </div>
  );
};

export default AdminCleaningPricingRules;
